import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Button,
  Card,
  Empty,
  Layout,
  Spin,
  Table,
  Typography,
} from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";

import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import { API_BASE_URL } from "../../utils/constants";
import { formatDateTime } from "../../utils/appUtils";
import "../../assets/styles/components_styles/InvitationRsvpsPage.css";

const { Content } = Layout;
const { Title, Text } = Typography;

function dash(v) {
  if (v == null || v === "") return "—";
  return v;
}

const RSVP_TABLE_COLUMNS = [
  {
    title: "Name",
    dataIndex: "guest_name",
    key: "guest_name",
    render: (v) => dash(v),
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    render: (v) => dash(v),
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    render: (v) => dash(v),
  },
  {
    title: "Guests",
    dataIndex: "guest_count",
    key: "guest_count",
    width: 100,
    align: "center",
  },
  {
    title: "Responded",
    dataIndex: "created_at",
    key: "created_at",
    render: (iso) => (iso ? formatDateTime(iso) : "—"),
  },
];

export default function InvitationRsvpsPage() {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setLoading(false);
      setError("Please sign in to view RSVP responses.");
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/invitation-designs/rsvp-responses`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        if (!cancelled) setGroups(res.data?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load RSVP responses.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const empty = !loading && !error && Array.isArray(groups) && groups.length === 0;

  return (
    <Layout className="invitation-rsvps-page">
      <HelmetWrapper
        title="Invitation RSVPs | PingVite"
        description="RSVP responses for your PingVite invitations."
        keywords="PingVite, invitation, RSVP"
      />
      <Header isHome={false} />
      <Content className="invitation-rsvps-page__content topheaderLayout">
        <div className="invitation-rsvps-page__inner">
          <Link to="/invitation-builder" className="invitation-rsvps-page__back">
            <ArrowLeftOutlined /> <span>Invitations</span>
          </Link>

          <Title level={2} className="invitation-rsvps-page__title">
            RSVP responses
          </Title>
          <Text type="secondary" className="invitation-rsvps-page__intro">
            Responses from your shared invitation links. Times are shown in your
            browser&apos;s local timezone.
          </Text>

          {loading && (
            <div className="invitation-rsvps-page__loading">
              <Spin size="large" />
            </div>
          )}

          {!loading && error && (
            <Alert type="error" showIcon message={error} style={{ marginTop: 24 }} />
          )}

          {!loading && !error && empty && (
            <Empty
              className="invitation-rsvps-page__empty"
              description="No RSVPs yet. Share your invitation link to collect responses."
            />
          )}

          {!loading &&
            !error &&
            groups.map((g) => (
              <InvitationRsvpGroupCard key={g.invitation_design_id} group={g} />
            ))}
        </div>
      </Content>
      <Footer />
    </Layout>
  );
}

function InvitationRsvpGroupCard({ group }) {
  return (
    <Card className="invitation-rsvps-page__card" bordered={false}>
      <div className="invitation-rsvps-page__card-head">
        <Title level={4} className="invitation-rsvps-page__card-title">
          {group.invitation_name || "Untitled invitation"}
        </Title>
        <Link to={`/invitation-builder/${group.invitation_design_id}`}>
          <Button type="primary" icon={<EditOutlined />}>
            Open in editor
          </Button>
        </Link>
      </div>
      <Table
        rowKey="id"
        columns={RSVP_TABLE_COLUMNS}
        dataSource={group.rsvps || []}
        pagination={false}
        size="middle"
        locale={{ emptyText: "No responses for this invitation yet." }}
      />
    </Card>
  );
}
